/* ═══════════════════════════════════════════════════════
   NYC Suite — Constants & Configuration
   ═══════════════════════════════════════════════════════ */

export const STUDIO_ADDRESS_PARTS = {
  venue: "New York Capture",
  address: "62 Flushing Ave",
  city: "Brooklyn",
  state: "NY",
  zip: "11205",
};
export const STUDIO_ADDRESS = `${STUDIO_ADDRESS_PARTS.venue}, ${STUDIO_ADDRESS_PARTS.address}, ${STUDIO_ADDRESS_PARTS.city} ${STUDIO_ADDRESS_PARTS.state} ${STUDIO_ADDRESS_PARTS.zip}`;
export const STUDIO_ADDRESS_LINES = [
  STUDIO_ADDRESS_PARTS.venue,
  STUDIO_ADDRESS_PARTS.address,
  `${STUDIO_ADDRESS_PARTS.city}, ${STUDIO_ADDRESS_PARTS.state} ${STUDIO_ADDRESS_PARTS.zip}`,
];

/* ═══ JOB STATUSES ═══ */
// Production pipeline (linear, left-to-right)
export const JOB_STATUSES = ["scheduled", "in_progress", "processing", "delivered"];
// Business milestones (independent toggles, admin-only)
export const JOB_MILESTONES = ["invoiced", "paid"];
// Combined for backward compat (filters, CSV export, etc.)
export const JOB_ALL_STATUSES = [...JOB_STATUSES, ...JOB_MILESTONES];
export const JOB_STATUS_LABELS = {
  scheduled: "Scheduled", in_progress: "In Progress",
  processing: "Processing", delivered: "Delivered", invoiced: "Invoiced", paid: "Paid"
};
export const JOB_STATUS_COLORS = (C) => ({
  scheduled: C.blue, in_progress: C.amber,
  processing: "#8B5CF6", delivered: C.green, invoiced: "#0D9488", paid: "#059669"
});
// Short descriptive sub-labels for pipeline tiles in the Jobs list.
export const JOB_STATUS_SUBLABEL = {
  scheduled: "upcoming",
  in_progress: "on set",
  processing: "in post",
  delivered: "recent",
  invoiced: "awaiting pay",
  paid: "closed out",
};

/* ═══ CAPTURE LOG STATUSES (per-scan row state) ═══ */
export const CAPTURE_LOG_STATUSES = ["pending", "captured"];
export const CAPTURE_LOG_STATUS_DEFAULT = "pending";

/* ═══ SESSION TYPES (multi-day jobs) ═══ */
// Array order IS the canonical sort order for same-date tie-break: flight → setup → capture → strike.
export const SESSION_TYPES = ["flight", "setup", "capture", "strike"];
export const DEFAULT_SESSION_TYPE = "capture";
// Sessions without capture activity — capture/processing/delivery tabs stay hidden for these.
export const NON_CAPTURE_SESSION_TYPES = ["flight", "setup", "strike"];
export const SESSION_TYPE_LABELS = {
  flight: "Flight",
  setup: "Setup",
  capture: "Capture",
  strike: "Strike",
};
// Purple (setup) reuses the "processing" hue to link setup/strike visually with the processing phase.
export const SESSION_TYPE_COLORS = (C) => ({
  flight: C.teal,
  setup: "#8B5CF6",
  capture: C.blue,
  strike: C.amber,
});

// Print-safe variants for PDF output (white background, higher contrast than the app palette).
export const SESSION_TYPE_PDF_COLORS = {
  flight: { bg: "rgba(13,148,136,0.10)", fg: "#0D9488" },
  setup: { bg: "rgba(139,92,246,0.10)", fg: "#6D44CC" },
  capture: { bg: "rgba(59,130,246,0.10)", fg: "#2563EB" },
  strike: { bg: "rgba(245,158,11,0.10)", fg: "#B45309" },
};

// Location fields that live on both a job and each of its sessions. Referenced by migrateJob()
// backfill and (in Phase 2) the per-session location editor.
export const SESSION_LOCATION_FIELDS = [
  "location", "locationVenue", "locationAddress",
  "locationCity", "locationState", "locationZip",
];

// Single-day root fields that also need to land on sessions[0] so calendar sync sees the change.
// `captureDate` maps to session field `date`; call/wrap times are mirrored verbatim.
export const SESSION_SCHEDULE_FIELDS = ["captureDate", "callTime", "wrapTime"];

/* ═══ EQUIPMENT / TRANSPORT / DELIVERY ═══ */
export const TRANSPORT_OPTIONS = ["In Studio", "Sprinter", "Box Truck", "Car Service", "Mobile (Peter or Ethan Car)", "Plane"];
export const BACKUP_OPTIONS = ["NAS1", "NAS2"];

/* ═══ PRE-PROD MASTER LISTS (crew/storage) ═══ */

/* ═══ CAMERA IDENTIFIERS (equipment names that have lenses) ═══ */
export const CAMERA_EQUIPMENT = ["Canon R5"];

/* ═══ LENS CATALOG — keyed by camera name ═══ */
export const LENS_CATALOG = {
  "Canon R5": [
    { id: "lens1", name: "RF 24-70mm f/2.8", short: "24-70mm" },
    { id: "lens2", name: "RF 100mm f/2.8 Macro", short: "100mm Macro" },
    { id: "lens3", name: "RF 16-35mm f/2.8", short: "16-35mm" },
    { id: "lens4", name: "RF 50mm f/1.4", short: "50mm f/1.4" },
    { id: "lens5", name: "RF 85mm f/1.4", short: "85mm f/1.4" },
  ],
};

/* ═══ EQUIPMENT CHIP COLORS ═══ */
export const EQUIP_CHIP_COLORS = {
  "Big Rig":                     { bg: "rgba(160,140,200,0.15)", border: "rgba(160,140,200,0.4)", text: "#a08cc8" },
  "Head Rig":                    { bg: "rgba(100,180,200,0.15)", border: "rgba(100,180,200,0.4)", text: "#64b4c8" },
  "Artec Leo":                   { bg: "rgba(74,144,217,0.15)",  border: "rgba(74,144,217,0.4)",  text: "#4a90d9" },
  "Artec Space Spider II":       { bg: "rgba(160,100,220,0.15)", border: "rgba(160,100,220,0.4)", text: "#a064dc" },
  "Artec Space Spider I":        { bg: "rgba(140,90,200,0.15)",  border: "rgba(140,90,200,0.4)",  text: "#8c5ac8" },
  "Canon R5":                    { bg: "rgba(198,169,122,0.15)", border: "rgba(198,169,122,0.4)", text: "#c6a97a" },
  "Leica RTC 360":               { bg: "rgba(124,169,130,0.15)", border: "rgba(124,169,130,0.4)", text: "#7ca982" },
  "Flying Fox":                  { bg: "rgba(217,140,74,0.15)",  border: "rgba(217,140,74,0.4)",  text: "#d98c4a" },
  "XGrids PortalCam":             { bg: "rgba(200,130,160,0.15)", border: "rgba(200,130,160,0.4)", text: "#c882a0" },
  "PortalCam":                    { bg: "rgba(200,130,160,0.15)", border: "rgba(200,130,160,0.4)", text: "#c882a0" }, // legacy — old jobs store this name
  "DJI Mavic 3E":                { bg: "rgba(140,200,180,0.15)", border: "rgba(140,200,180,0.4)", text: "#8cc8b4" },
  "Scanology NimbleTrack Gen 2": { bg: "rgba(180,160,130,0.15)", border: "rgba(180,160,130,0.4)", text: "#b4a082" },
};
export const EQUIP_CHIP_DEFAULT = { bg: "rgba(150,150,150,0.15)", border: "rgba(150,150,150,0.4)", text: "#999" };

export const LENS_CHIP_COLORS = {
  "RF 24-70mm f/2.8":    { bg: "rgba(217,170,100,0.15)", border: "rgba(217,170,100,0.35)", text: "#d9aa64" },
  "RF 100mm f/2.8 Macro": { bg: "rgba(200,120,140,0.15)", border: "rgba(200,120,140,0.35)", text: "#c8788c" },
  "RF 16-35mm f/2.8":    { bg: "rgba(140,190,210,0.15)", border: "rgba(140,190,210,0.35)", text: "#8cbed2" },
  "RF 50mm f/1.4":       { bg: "rgba(180,200,140,0.15)", border: "rgba(180,200,140,0.35)", text: "#b4c88c" },
  "RF 85mm f/1.4":       { bg: "rgba(210,160,190,0.15)", border: "rgba(210,160,190,0.35)", text: "#d2a0be" },
};
export const LENS_CHIP_DEFAULT = { bg: "rgba(180,160,130,0.15)", border: "rgba(180,160,130,0.35)", text: "#b4a082" };

export const CREW_ROSTER = [
  { name: "Peter", email: "", phone: "", receivesAllUpdates: false },
  { name: "Ethan", email: "", phone: "", receivesAllUpdates: false },
  { name: "Ivin", email: "", phone: "", receivesAllUpdates: false },
  { name: "Jason", email: "", phone: "", receivesAllUpdates: false },
  { name: "Sam", email: "", phone: "", receivesAllUpdates: false },
];
export const KV_KEY_GCAL_INTEGRATION = "integrations_google_calendar";
export const KV_KEY_CREW = "pp-crew";

export const STORAGE_OPTIONS = ["NAS1", "NAS2", "External SSD A", "External SSD B", "Local Workstation"];
export const DELIVERY_METHODS = ["box.com", "WeTransfer", "Client FTP", "Aspera", "SFTP", "Google Drive", "Hard Drive"];

/* ═══ RATE CATALOG ═══ */
export const RATE_CATALOG = {
  sessions: [
    // One standard studio rate for every industry (Ivin 2026-07-01) — the film/TV tier
    // ($1,800/$3,600) is RETIRED. Ids keep the historical "_art" suffix so existing
    // quotes and the AI prompt stay stable; labels are industry-neutral.
    { id: "half_art", label: "Partial Day Studio Session", rate: 1200, notes: "Up to 2 hours" },
    { id: "full_art", label: "Full Day Studio Session", rate: 2400, notes: "8 hours. 4 processed model minimum." },
    { id: "partial_mobile", label: "Partial Day Mobile Session - Custom Equipment", rate: 800 },
    { id: "full_onlocation", label: "Full Day on-location Photogrammetry Session", rate: 5500, notes: "Includes same day installation/strike\n3 hour setup time / 1.5 hour strike time\nSite requirements will be provided separately" },
  ],
  scanning: [
    { id: "head_scan", label: "Head Scan Capture + Processing", rate: 900, tiered: true, notes: "Volume pricing available, see attached tier sheet" },
    { id: "body_scan", label: "Full Body Scan Capture + Processing", rate: 1200, tiered: true, notes: "Volume pricing available, see attached tier sheet" },
    // torso_scan ($600) and other_body ($425) retired 2026-07-06 — never real rig categories.
    // Body parts are handheld/Leo at $300 (leo_torso / leo_other); old ids remap in applyAIResult.
    { id: "preview_model", label: "Preview Model Processing", rate: 35, notes: "USDZ / Lo-Res OBJ" },
  ],
  leo: [
    { id: "leo_body", label: "Full Body Scan (Leo)", rate: 600, notes: "Handheld scanner, no session fee" },
    { id: "leo_head", label: "Head Scan (Leo)", rate: 300 },
    { id: "leo_hands", label: "Hands Scan (Leo)", rate: 300 },
    { id: "leo_torso", label: "Torso Scan (Leo)", rate: 300 },
    { id: "leo_other", label: "Other Body Scan (Leo)", rate: 300 },
  ],
  lowpoly: [
    // Low Poly Deliverable tier (Ivin 2026-08-25, CLO3D / virtual-fitting inquiries):
    // reduced 1-hour session + raw reconstructed model. Cleanup, decimation, and
    // retopology are the client's responsibility — never bundled into this tier.
    { id: "lowpoly_session", label: "Low Poly Studio Session (1 Hour)", rate: 600, notes: "1 hour capture window on the photogrammetry rig" },
    { id: "lowpoly_body", label: "Low Poly Full Body Model", rate: 600, notes: "Raw reconstructed model, up to 1M polygons, OBJ or FBX. Cleanup, decimation, and retopology not included." },
  ],
  objects: [
    { id: "obj_jewelry_std", label: "Object, under 3 in (standard)", rate: 200 },
    { id: "obj_jewelry_mod", label: "Object, under 3 in (detailed)", rate: 300 },
    { id: "obj_jewelry_cpx", label: "Object, under 3 in (complex)", rate: 475 },
    { id: "obj_utensil_std", label: "Object, 3–8 in (standard)", rate: 275 },
    { id: "obj_utensil_mod", label: "Object, 3–8 in (detailed)", rate: 400 },
    { id: "obj_utensil_cpx", label: "Object, 3–8 in (complex)", rate: 600 },
    { id: "obj_shoe_std", label: "Object, 8–14 in (standard)", rate: 400 },
    { id: "obj_shoe_mod", label: "Object, 8–14 in (detailed)", rate: 600 },
    { id: "obj_shoe_cpx", label: "Object, 8–14 in (complex)", rate: 900 },
    { id: "obj_basketball_std", label: "Object, 14–24 in (standard)", rate: 500 },
    { id: "obj_basketball_mod", label: "Object, 14–24 in (detailed)", rate: 725 },
    { id: "obj_basketball_cpx", label: "Object, 14–24 in (complex)", rate: 1000 },
    { id: "obj_suitcase_std", label: "Object, 2–4 ft (standard)", rate: 575 },
    { id: "obj_suitcase_mod", label: "Object, 2–4 ft (detailed)", rate: 825 },
    { id: "obj_suitcase_cpx", label: "Object, 2–4 ft (complex)", rate: 1100 },
    { id: "obj_chair_std", label: "Object, 3–5 ft (standard)", rate: 600 },
    { id: "obj_chair_mod", label: "Object, 3–5 ft (detailed)", rate: 900 },
    { id: "obj_chair_cpx", label: "Object, 3–5 ft (complex)", rate: 1300 },
    { id: "obj_motorcycle_std", label: "Object, 5–7 ft (standard)", rate: 950 },
    { id: "obj_motorcycle_mod", label: "Object, 5–7 ft (detailed)", rate: 1400 },
    // obj_car (Vehicle-Scale, $3200) retired 2026-07-06 — vehicles route to the LiDAR estimator
    // (applyAIResult drops the id and raises lidarDetected). There is no catalog vehicle rate.
  ],
  environments: [
    { id: "env_portalcam", label: "XGrids PortalCam Gaussian Splatting", rate: 0, notes: "Rate varies by sqft: $0.25/sqft (<2k), $0.18/sqft (2-10k), $0.15/sqft (10k+), min $200" },
    // LiDAR is intentionally NOT in the catalog — it is priced ONLY via the LiDAR estimator
    // (LIDAR_MATRIX + computeLidarLineItems), so the catalog and AI cannot insert a $0
    // env_lidar line. hasLidarLineItem() still recognizes the legacy env_lidar /
    // env_lidar_photo ids so archived quotes that used them still get the rate-sheet addendum.
    { id: "env_nimble_onloc", label: "On-Location Scanning Session, Partial Day (up to 3 hrs)", rate: 1400, notes: "Handheld scanning crew on site. Deliverables billed per model on top." },
    { id: "env_onloc_full", label: "On-Location Scanning Session, Full Day (3-8 hrs)", rate: 1900, notes: "Handheld scanning crew on site. Deliverables billed per model on top." },
  ],
  addons: [
    { id: "extra_captures", label: "Additional captures over 10", rate: 50 },
    { id: "additional_processing", label: "Additional Model Processing", rate: 1200 },
    { id: "advanced_setup", label: "1 Day Advanced Setup", rate: 1000 },
    { id: "truck", label: "Travel Fee (transport not provided)", rate: 2500, notes: "NYC metro minimum, adjusted for further locations. Film transport is normally provided by production teamsters; this fee applies only when transport is not provided." },
    { id: "travel_local", label: "Travel, Local", rate: 100, notes: "Brooklyn, Manhattan, W Queens, Bronx" },
    { id: "travel_mid", label: "Travel, Mid", rate: 200, notes: "Staten Island, E Queens, close NJ, lower Westchester" },
    // Drone permit coordination (Ivin, 2026-09-04): the optional line for a client who wants
    // New York Capture to carry the permit. Absent, production holds the permit and the drone
    // liability insurance and New York Capture supplies the Part 107 certificate and the
    // aircraft registration (DRONE_MODEL, docs/pricing/drone-pricing.md).
    { id: "drone_permit", label: "Drone permit coordination", rate: 500, notes: "New York Capture prepares and files the NYPD take-off and landing permit or the FAA airspace authorization and coordinates the insurance certificate with production. 30 days lead time." },
  ],
};

/* ═══ VOLUME PRICING ═══ */
export const VOLUME_TIERS = {
  head_scan: [
    { min: 1, max: 1, rate: 900 },
    { min: 2, max: 4, rate: 850 },
    { min: 5, max: 7, rate: 750 },
    { min: 8, max: 9, rate: 700 },
    { min: 10, max: Infinity, rate: 600 },
  ],
  body_scan: [
    { min: 1, max: 1, rate: 1200 },
    { min: 2, max: 4, rate: 1100 },
    { min: 5, max: 7, rate: 950 },
    { min: 8, max: 9, rate: 850 },
    { min: 10, max: Infinity, rate: 750 },
  ],
};

/* ═══ SESSION FEE POLICY ═══ */
// How each catalog line relates to a studio session fee. Session fees apply
// ONLY to camera-array capture (Big Rig / Head Rig) — handheld and object
// per-scan rates are all-in (Ivin 2026-07-03). The AI guard in ai.js uses this
// to decide whether an extracted studio session line is legitimate, so every
// RATE_CATALOG id MUST be classified here (enforced by an exhaustiveness test).
//   session            — a session/day fee line itself
//   array_capture      — rig capture that legitimately carries a session fee
//   standalone_capture — all-in capture work; never carries a session fee
//   addon              — neutral (travel, processing, setup); says nothing
//                        about whether a session fee belongs on the quote
const SESSION_FEE_POLICY_BY_CATEGORY = {
  sessions: "session",
  scanning: "array_capture",
  leo: "standalone_capture",
  lowpoly: "standalone_capture",
  objects: "standalone_capture",
  environments: "standalone_capture",
  addons: "addon",
};
const SESSION_FEE_POLICY_EXCEPTIONS = {
  preview_model: "addon",     // per-capture processing, not capture work
  lowpoly_session: "addon",   // the reduced session that legitimately pairs with
                              // standalone lowpoly work — neutral so the guard
                              // never strips it and it never triggers stripping
};
export const SESSION_FEE_POLICY = Object.fromEntries(
  Object.entries(RATE_CATALOG).flatMap(([category, items]) =>
    items.map((it) => [it.id, SESSION_FEE_POLICY_EXCEPTIONS[it.id] || SESSION_FEE_POLICY_BY_CATEGORY[category] || "addon"])
  )
);

/* ═══ LIDAR PRICING MATRIX v1 (RTC 360) — FROZEN, RETIRED 2026-09-03 ═══ */
// The tier matrix that priced LiDAR from 2026-07-01 to 2026-09-03. It is NOT a pricing
// source any more: computeLidarLineItems() prices from LIDAR_MODEL below. It stays here,
// unchanged, for exactly one reader: the v1 rate-sheet addendum that archived quotes
// (LiDAR lines with no `lidar.model` stamp) keep printing, so a re-exported PDF of a
// quote a client already received never shows rates that disagree with its priced line
// (Ivin, 2026-09-03). `null` = physically-impossible cell; never use Infinity here (a
// persisted value would corrupt to null through Firestore JSON). Do not edit these
// numbers and do not add new readers.
export const LIDAR_MATRIX = {
  // Capture only (raw scans, no processing) — priced by TIME only; setups don't affect price.
  captureOnly: {
    partial:  { lidar: 1400, photo: 2000 },  // 0–3 hrs
    full:     { lidar: 1900, photo: 3500 },  // 3–8 hrs
    overtime: { lidar: 350,  photo: 550 },   // per hr past 8
  },
  // Capture + processing — priced by TIME × SETUP TIER. photo = +photogrammetry.
  processing: {
    partial: {
      "<=25":  { lidar: 1900, photo: 2850 },
      "26-60": { lidar: 2600, photo: 3900 },
      "61-99": null,   // n/a — can't run 61+ setups in under 3 hrs
      "100+":  null,   // n/a
    },
    full: {
      "<=25":  { lidar: 2500, photo: 3750 },
      "26-60": { lidar: 3200, photo: 4800 },
      "61-99": { lidar: 3900, photo: 5850 },
      "100+":  { lidar: 4600, photo: 6900 },
    },
    overtime: { lidar: 350, photo: 550 },    // per hr past 8
  },
  highDensityMultiplier: 1.5,  // ×1.5 whole day when 35%+ of scans are high-density; overtime scales ×1.5 too ($350→$525, $550→$825)
  defaultProcessingDays: 5,    // typical business days to process capture + processing (editable per quote; scales with scope)
  setupTiers: ["<=25", "26-60", "61-99", "100+"],
  timeBands: { partial: "0–3 hrs", full: "3–8 hrs" },
  // Equipment specs for the rate-sheet addendum (page 2).
  equipment: {
    rtc360: {
      model: "Leica RTC 360",
      type: "Terrestrial laser scanner",
      specs: {
        "Scan rate": "2 million pts/sec",
        "3D point accuracy": "1.9 mm @ 10 m",
        "Range": "0.5–130 m",
        "Scan time": "Full dome < 2 min",
        "Imaging": "432 MP spherical HDR",
        "Registration": "Automated VIS (targetless)",
      },
    },
    r5: {
      model: "Canon R5 (or equivalent)",
      type: "Full-frame camera · photographic texture",
      specs: {
        "Model": "Canon EOS R5",
        "Type": "Full-frame mirrorless",
        "Sensor": "36 × 24 mm CMOS",
        "Resolution": "~45 MP",
        "Continuous shooting": "Up to 20 fps",
        "Stabilization": "IBIS, up to 8 stops",
        "Lens mount": "Canon RF",
      },
    },
  },
};

/* ═══ LIDAR PRICING MODEL v2 (RTC 360) ═══ */
// Replaces the LIDAR_MATRIX tier grid as the calculator's source of truth (Ivin,
// 2026-09-03). LIDAR_MATRIX stays exported ONLY for the frozen v1 addendum that
// archived quotes print — never price from it. Canonical record:
// docs/pricing/lidar-pricing.md. Client sheet: nyc_website/lidar-rate-sheet.html.
// Market + history check (2026-09-03): capture lands mid-market for NYC field crews
// ($185-$350/hr); v2 totals sit within ~15% of what NYC actually invoiced for
// comparable 2024-2025 days, without being calibrated to them.
//
// Why the grid was retired:
//  - Its two tables were derived by different rules (capture-only from the $200/hr
//    person floor, capture+processing as a flat 1.5x of the LiDAR cell). Deferred
//    processing bills as the DIFFERENCE between them, which produced $250 to register
//    25 setups and deliver a textured OBJ.
//  - Tier steps meant setup 26 cost $1,050 more than setup 25 (x1.5 under high density).
//  - The 100+ bucket had no ceiling, so 130 setups priced identically to 100.
//  - Crew size was encoded as a boolean (photogrammetry on = a second shooter), and the
//    high-density x1.5 was being used as a de-facto stand-in for a third person. Crew is
//    now an explicit input, which is what the multiplier was really doing.
export const LIDAR_MODEL = {
  // CAPTURE — a crew-scaled day rate. Works out to ~$170 per person-hour on a 3-crew
  // film day, against a market field rate of $185-$350/hr: you do not bill three leads
  // at lead rate.
  capture: {
    baseDay: 2200,             // lead operator + RTC 360 + vehicle and overhead
    perAdditionalCrew: 1500,   // each head beyond the lead, for the whole day
    // A day is 10 hrs for film/TV and 8 hrs everywhere else (Ivin, 2026-09-03). The
    // band is the unit of work being sold, not a rate change — film pays the same day
    // rate for the longer standard day, and the linear processing rate keeps a
    // low-output standby day from overcharging.
    dayHours: { film: 10, standard: 8 },
    partialHours: 3,
    partialDay: 1400,          // 0-3 hrs, lead operator only
    overtimePerCrewHour: 200,  // past the day length, per crew member (Ivin's person floor)
  },

  // PROCESSING — linear per setup. Registration plus a textured OBJ, which sits above
  // bare registration ($500-$1,000 as a market add-on) and well below scan-to-BIM.
  // Checks out against hours: ~6-10 hrs for a 40-setup job at a $75-$150/hr office rate.
  processing: {
    perSetup: { lidar: 25, photo: 35 },
    minimum:  { lidar: 500, photo: 700 },
    defaultDays: 5,
    // Setups included above the quoted count before a change order. The quote bills the
    // top of the estimated range; this is the headroom that keeps a few extra positions
    // from triggering a re-quote.
    graceSetups: 5,
  },

  // RUSH — processing begun immediately after capture, typically across a weekend.
  // 50% is time-and-a-half on the labor actually being bought. A straight percentage,
  // no floor: Ivin dropped the $750 minimum 2026-09-03 ("let's just do 50%"). The
  // field stays so a floor can come back as one number; 0 means none.
  rush: {
    processingMultiplier: 0.5,
    minimum: 0,
    turnaroundDays: 1,   // the toggle sets turnaround here as well as adding the fee
  },

  // HIGH DENSITY — demoted to a processing-side modifier. Under the old fused matrix the
  // x1.5 was carrying three jobs at once: crew size, job size, and per-scan density. Crew
  // is now its own field and job size is metered per setup, so multiplying the whole day
  // would charge twice for facts already priced. What remains is heavier files per scan.
  // Approved by Ivin 2026-09-03.
  highDensity: { processingMultiplier: 1.15 },

  equipment: LIDAR_MATRIX.equipment,
};

/* ═══ RATE-SHEET EQUIPMENT CARDS (gear beyond the LiDAR kit) ═══ */
// The v2 LiDAR addendum's equipment page lists only gear the quote uses (Ivin,
// 2026-10-20: a LiDAR-only quote printed the Canon R5). lidarSheetEquipment() prints
// the RTC 360 always, the R5 only when a LiDAR line has photogrammetry on, then each
// card below that the quote uses: a line with one of its catalogIds, or one of its
// equipmentIds in the quote's equipment list. A card stays hidden until it is
// `verified` and has a model, an image (a file name in src/assets/equipment/) and at
// least one spec row. Specs are never invented: Ivin supplies the photo and the specs
// and flips `verified`.
export const SHEET_EQUIPMENT = {
  portalcam: {
    model: "XGrids PortalCam",
    type: null,
    image: null,
    specs: {},
    verified: false,
    catalogIds: ["env_portalcam"],
    equipmentIds: ["portal"],
  },
  // The 360 camera Ivin calls the "X6". The exact model name is unconfirmed, so nothing
  // client facing is written yet. It has no catalog line or equipment id; add one to
  // the lists below once it exists.
  camera360: {
    model: null,
    type: null,
    image: null,
    specs: {},
    verified: false,
    catalogIds: [],
    equipmentIds: [],
  },
};

/* ═══ DRONE PRICING MODEL v1 (DJI Mavic 3E) ═══ */
// Approved by Ivin 2026-09-04 (mockups/drone-pricing-v1.html). The same shape as
// LIDAR_MODEL: a crew-scaled capture day for the Part 107 pilot plus the aircraft,
// processing metered per acre flown with a floor, rush as a share of processing.
// computeDroneLineItems() prices from it and DroneRateSheet prints from it. Canonical
// record: docs/pricing/drone-pricing.md.
//
// The capture numbers are the LiDAR numbers, written out rather than referenced: one
// price for a lead operator day regardless of instrument (decision 2), but a future
// change to either day rate should be a decision about both, not a silent side effect
// of editing the other. droneModel.test.js pins the two capture blocks equal so drift
// is caught.
//
// Combined days: when the RTC 360 leads the day, the pilot is one additional crew member
// on the LiDAR estimator ($1,500) and there is no aircraft charge (the 9/12 Fresh Kills
// day: $2,200 + $1,500). When the drone leads, the base day covers pilot and aircraft.
// That is a quoting rule for the operator, not code.
export const DRONE_MODEL = {
  // CAPTURE — Part 107 pilot with the Mavic 3E, batteries and controller.
  capture: {
    baseDay: 2200,             // lead operator day: pilot + aircraft
    perAdditionalCrew: 1500,   // visual observer or second operator, for the whole day
    dayHours: { film: 10, standard: 8 },
    partialHours: 3,
    partialDay: 1400,          // 0 to 3 hrs on site; additional crew is billed per day here too (Ivin, 2026-09-04); never reaches overtime
    overtimePerCrewHour: 200,  // each hour past the day length, per crew member
  },

  // PROCESSING — photogrammetry from the drone stills in RealityCapture, delivered raw.
  // geometry = matchmove: a scaled point cloud plus mesh, no textures. textured = a
  // textured mesh from the same stills. Acres are a job total, so processing never
  // multiplies by days. Set by Ivin 2026-09-04: $70 reproduces the 9/12 Fresh Kills
  // figure within $5, $100 reproduces GU3 exactly at 10 acres, and the floors are the
  // LiDAR floors (opening a project costs the same office time on 2 acres as on 7).
  // There is no high-density analog.
  processing: {
    perAcre: { geometry: 70, textured: 100 },
    minimum:  { geometry: 500, textured: 700 },
    defaultDays: 5,
    // Acreage included above the quoted figure before a change order: quoted acres plus
    // 10 percent. LiDAR's equivalent is 5 setups of grace; a share scales with the site.
    graceShare: 0.1,
  },

  // RUSH — processing begun immediately after capture. Mirrors LiDAR: 50% of processing,
  // no minimum, and the toggle sets the turnaround as well as the fee. Never applies to
  // capture only.
  rush: {
    processingMultiplier: 0.5,
    minimum: 0,
    turnaroundDays: 1,
  },

  aircraft: "DJI Mavic 3E",
};

// Every line computeDroneLineItems() emits carries drone.model = DRONE_MODEL_VERSION, so a
// later model can keep printing this sheet for quotes priced on it (the LiDAR v1 freeze).
export const DRONE_MODEL_VERSION = "v1";

/* ═══ CAMERA ARRAY (BIG RIG / HEAD RIG) SHEET DATA ═══ */
// Canonical record: docs/pricing/big-rig-pricing.md (locked with Ivin 2026-07-01).
// The $5,500 on-location day and $1,200/$2,400 studio sessions live in RATE_CATALOG;
// these are the terms that have no catalog line, plus the equipment specs for the
// Camera Array Rate Sheet addendum. Specs mirror nyc_website EQ_DETAIL (Ivin's data).
export const ARRAY_SHEET = {
  consecutiveDayRate: 2750, // each back-to-back on-location day after day 1 (50% of $5,500)
  overtimeHourRate: 500,    // per hr past 8 capture hours; active capture time only
  rawCapturePreviewRate: 35, // per capture; applies past 5 captures w/ single model; film/TV excluded
  equipment: {
    bigRig: {
      model: "Big Rig",
      tag: "175-Camera Array",
      why: "Our largest camera array captures the entire body from every angle in a single instant. It was built in-house for people and animals that cannot hold still for a handheld scanner.",
      specs: {
        "Cameras": "175 × Canon T7i",
        "Resolution": "24.2 MP per camera",
        "Capture speed": "1/60 second",
        "Lighting": "Strobe",
        "Coverage": "Full 360°",
        "Output": "RAW + JPEG, full color",
        "Footprint": "~20×20 ft",
        "Setup / strike": "~3 hrs / ~90 min",
        "Power": "100A lunchbox, 5× 15–20A circuits",
      },
    },
    headRig: {
      model: "Head Rig",
      tag: "144-Camera Array",
      why: "The Head Rig is a tighter array built for close-range detail: faces, hands, and small animals. It captures fine skin texture, pores, and individual hair follicles, with the same instant capture as the Big Rig focused on a smaller subject.",
      specs: {
        "Cameras": "144 × Canon T7i",
        "Resolution": "24.2 MP per camera",
        "Capture speed": "1/60 second",
        "Lighting": "Strobe",
        "Coverage": "Full 360°",
        "Output": "RAW + JPEG, full color",
        "Footprint": "~10×15 ft",
        "Setup / strike": "~3 hrs / ~90 min",
        "Power": "100A lunchbox, 5× 15–20A circuits",
      },
    },
  },
};

/* ═══ DELIVERABLE FORMAT PRESETS ═══ */
export const DELIVERABLE_FORMATS = [
  { id: "obj", label: "Textured OBJ", desc: "OBJ with texture maps" },
  { id: "fbx", label: "FBX", desc: "FBX" },
  { id: "usdz", label: "USDZ", desc: "USDZ" },
  { id: "glb", label: "GLB / glTF", desc: "GLB/glTF" },
  { id: "splat", label: "Gaussian Splat", desc: "Gaussian splat (.ply / .splat)" },
  { id: "pointcloud", label: "Point Cloud", desc: "Point cloud" },
  { id: "stl", label: "STL", desc: "STL" },
  { id: "raw_photos", label: "Raw Photos", desc: "Raw capture photography" },
];

/* ═══ DOCUMENT TYPES ═══ */
export const DOC_TYPES = {
  quote: { label: "Quote Sheet", headerText: (n) => `Quote Sheet Prepared for ${n}`, desc: "Fixed bid." },
  rate_sheet: { label: "Rate Sheet", headerText: (n) => `Rate Sheet Prepared for ${n}`, desc: "Per-unit rates." },
  estimate: { label: "Estimate", headerText: (n) => `Quote Sheet Prepared for ${n}`, desc: "Flex scope." },
  change_order: { label: "Change Order", headerText: (n) => `Change Order Prepared for ${n}`, desc: "Additional scope." },
};

/* ═══ QUOTE SECTIONS ═══ */
// Three visibility modes:
//   alwaysOn  — locked legal clause, prints on every quote, no toggle.
//   defaultOn — prints unless the operator explicitly switches it off
//               (sections[key].enabled === false). Legacy quotes with no stored
//               entry keep printing it.
//   (neither) — opt-in: prints only when sections[key].enabled === true.
// `generated: true` marks sections whose body is built from quote fields at
// render time (no editable text — the builder shows a toggle + preview only).
export const SECTION_DEFS = {
  expiration: { label: "Quote Expiration", defaultText: "This quote is valid for 30 days from the date of issue.", alwaysOn: true },
  fileFormats: { label: "File Formats", defaultText: "Standard deliverables include OBJ with texture maps. Additional formats available upon request: FBX, USDZ, GLB/glTF, STL, PLY. Gaussian splat output (.ply / .splat) available for real-time applications. Custom deliverables are available, please inquire.", defaultOn: true },
  turnaround: { label: "Turnaround", defaultText: "", defaultOn: true, generated: true },
  nda: { label: "Confidentiality", defaultText: "New York Capture treats all client projects as confidential. A mutual NDA can be provided upon request. No client data, images, or 3D models will be shared publicly or with third parties without written consent.", alwaysOn: true },
  payment: { label: "Payment", defaultText: "Payment is processed through Intuit. All standard payment methods are accepted, including credit card, ACH, and bank transfer.", alwaysOn: true },
  // Two catalog sentences: `includedText` prints when the quote carries a travel line item,
  // `defaultText` otherwise (travelClauseText in builderHelpers). Operator-typed text wins.
  travel: { label: "Travel / Parking / Per Diem", defaultText: "Travel will be quoted separately and may include transportation, parking, lodging, and per diem as applicable.", includedText: "Travel for each capture day is included as a line item above.", alwaysOn: false },
  cancellation: { label: "Cancellation / Reschedule", defaultText: "Cancellations made less than 24 hours before the scheduled session are subject to a fee equal to 100% of the session rate. Rescheduling is available at no charge with reasonable advance notice.", alwaysOn: false },
  projectTerms: { label: "Project Terms", defaultText: "", alwaysOn: false },
  dataDelivery: { label: "Data Delivery", defaultText: "Data will be delivered via client-supplied FTP, box.com, or WeTransfer.", defaultOn: true },
};

/* ═══ ENTITY & NETWORK TYPES ═══ */
export const INDUSTRY_LABELS = {
  film_tv: "Film / TV",
  commercial_product: "Commercial / Product",
  music: "Music",
  fashion: "Fashion",
  gaming: "Gaming",
  spatial: "Spatial (VR/AR/XR)",
  museum_archival: "Museum / Archival",
  visual_art: "Visual Art",
  gallery: "Gallery",
  engineering_manufacturing: "Engineering / Manufacturing",
  architecture_real_estate: "Architecture / Real Estate",
  education_research_institutional: "Education / Research / Institutional",
  medical: "Medical",
  theatre: "Theatre",
  other: "Other",
};
export const CONTACT_TIERS = { hot: "Hot", warm: "Warm", cool: "Cool", review: "Review", client: "Client", junk: "Junk" };
export const TIER_COLORS = { hot: "#EF4444", warm: "#F59E0B", cool: "#6B7280", review: "#9CA3AF", client: "#0D9488", junk: "#9B9B9B" };
export const CONTACT_STATUSES = { new: "New", contacted: "Contacted", qualified: "Qualified", converted: "Converted", lost: "Lost" };
export const LEAD_SOURCES = { referral: "Referral", website: "Website", cold: "Cold", repeat: "Repeat Client", partner: "Partner", social: "Social Media", conference: "Conference", other: "Other" };
export const JOB_ROLES = { billed_client: "Billed Client", contact: "Contact", producer: "Producer", vfx_supervisor: "VFX Supervisor", vendor: "Vendor", artist: "Artist", agency: "Agency", referral: "Referral", other: "Other" };
export const STATUS_COLORS_MAP = (C) => ({ quoted: C.amber, booked: C.blue, completed: C.green, lost: C.danger, archived: "#6B7280" });

/* ═══ CLIENT / CONTACT (v2 data model) ═══ */
export const CLIENT_STATUSES = { active: "Active", inactive: "Inactive", closed: "Closed" };
export const CONTACT_ROLES = {
  customer: "Customer", prospect: "Prospect", qualified: "Qualified",
  ally: "Ally", friend: "Friend", vendor: "Vendor",
  collaborator: "Collaborator", referrer: "Referrer",
};
export const CLIENT_STATUS_COLORS = (C) => ({
  active: C.green, inactive: C.amber, closed: C.danger,
});
export const ROLE_COLORS = {
  customer: "#10B981", prospect: "#F59E0B", qualified: "#3B82F6",
  ally: "#0D9488", friend: "#8B5CF6", vendor: "#6B7280",
  collaborator: "#D97706", referrer: "#EC4899",
};

/* ═══ CRM / CONSENT ═══
   Three states replace the five Mailchimp statuses (CRM v3 decision 7,
   2026-09-06). Bulk mail goes to `opted_in` and `relationship` only; `no_bulk`
   never receives it. `MAILCHIMP_STATUSES` below stays exported for reading
   legacy records — nothing new writes `mailchimpStatus`. */
export const CONSENT_STATES = {
  opted_in:     "Opted in",       // website mailing-list checkbox, or a real Mailchimp subscribe
  relationship: "Relationship",   // quoted or booked, the actual person on the thread, never a cc
  no_bulk:      "No bulk",        // cc'd, vendors, unknown, or anyone who unsubscribed
};
export const CONSENT_COLORS = (C) => ({ opted_in: C.green, relationship: C.blue, no_bulk: C.muted });
/* Handles carried on contacts and clients. `linkedin` on a contact is an
   object ({url, connected, connectedOn}) because the LinkedIn CSV import
   stamps whether Ivin is actually connected; the rest are plain strings. */
export const SOCIAL_KEYS = ["linkedin", "instagram", "x", "website"];

/* ═══ CRM / MAILCHIMP (legacy — read only) ═══ */
export const MAILCHIMP_STATUSES = {
  never_sent: "Never Sent", active: "Active", inactive: "Inactive",
  unsubscribed: "Unsubscribed", bounced: "Bounced",
};
export const CAMPAIGN_GOALS = {
  awareness: { label: "Awareness", ctaText: "Visit Our Website", ctaUrl: "https://newyorkcapture.com" },
  website: { label: "Drive to Website", ctaText: "Explore Our Work", ctaUrl: "https://newyorkcapture.com" },
  booking: { label: "Book a Call", ctaText: "Book a Free Consultation", ctaUrl: "" },
  reply: { label: "Get a Reply", ctaText: "Reply to This Email", ctaUrl: "" },
};
export const CRM_INTERNAL_EMAILS = [
  "ivin@nycap3d.com", "ivinballen@gmail.com", "info@nycap3d.com",
  "ivin@lenscloud3d.com", "info@lenscloud3d.com",
  "hello@newyorkcapture.com", "ivin@newyorkcapture.com",
  "ethan@nycap3d.com", "richtarech@nycap3d.com",
];

/* ═══ DOCUMENT TYPES ═══ */
export const DOC_CATEGORIES = {
  site_requirements: { label: "Site Requirements", desc: "Tech specs & space requirements for on-location scans", icon: "📐" },
  pre_scan: { label: "Pre-Scan Instructions", desc: "Client prep guide for scan day", icon: "📋" },
  scope_of_work: { label: "Scope of Work", desc: "Project brief with deliverables & timeline", icon: "📄" },
  general: { label: "General Document", desc: "Custom document with NYCAP letterhead", icon: "📝" },
  contact_sheet: { label: "Contact Sheet", desc: "PDF contact sheet from NYC Ingest", icon: "📸" },
};

/* ═══ LIFECYCLE STAGES & DOC TEMPLATE TYPES ═══ */
export const LIFECYCLE_STAGES = {
  post_booking: "Post-Booking",
  day_before: "Day Before",
  post_shoot: "Post-Shoot",
  post_delivery: "Post-Delivery",
};

export const DOC_TEMPLATE_TYPES = {
  billing_agreement: "Billing Agreement",
  project_brief: "Project Brief",
  deliverable_requirements: "Deliverable Requirements",
  day_before_confirmation: "Day-Before Confirmation",
  contact_sheet: "Contact Sheet",
  job_report: "Job Report",
};

export const CONTENT_BLOCK_TAGS = {
  studio: "Studio",
  on_location: "On-Location",
  head_scan: "Head Scan",
  body_scan: "Body Scan",
  object_scan: "Object Scan",
  lidar: "LiDAR",
  gaussian_splat: "Gaussian Splat",
  leo: "Artec Leo",
  wardrobe: "Wardrobe",
  logistics: "Logistics",
  policy: "Policy",
  technical: "Technical",
  billing: "Billing",
};

/* ═══ NYCAP DOCUMENT STYLE GUIDE ═══ */
export const NYCAP_DOC_STYLE = `You are a document writer for New York Capture LLC (newyorkcapture.com).
Company: 3D scanning and photogrammetry studio, Brooklyn, NYC.
Services: Photogrammetry, structured-light scanning (Artec Space Spider II, Artec Leo), on-location and in-studio capture.
Clients: VFX houses, fashion brands, museums, galleries, production companies, game studios.

VOICE & TONE:
- Confident, direct, professional. Not corporate or stiff.
- Technical where appropriate — use correct terminology (photogrammetry, structured-light, mesh, texture map, USDZ, OBJ, FBX).
- Concise. Every sentence should earn its place. Cut filler words.
- Active voice. "We will capture" not "Capturing will be performed."
- Second person for client-facing docs ("You will receive..."), first person plural for NYCAP actions ("We will set up...").

FORMATTING RULES:
- Section headers: Short, descriptive, title case. End with no punctuation. Example: "Space Requirements" not "SPACE REQUIREMENTS:" or "Requirements for the Space:"
- Use bullet points (- ) for lists of 3+ items. Keep bullets to one line each when possible.
- Use numbered lists (1. 2. 3.) only for sequential steps or priorities.
- Separate sections with a blank line. No more than 2-3 sentences per paragraph.
- Never use bold, italic, or markdown formatting marks (**, *, ##). The preview renderer handles styling automatically from structure.
- Section headers go on their own line followed by a blank line, then content.

DOCUMENT STRUCTURE BY CATEGORY:

Site Requirements:
- Open with a one-line summary of the session type and talent/object count
- Sections: Space Requirements, Power & Electrical, Lighting, Wardrobe/Prep Area (if talent), Access & Loading, Timeline, Contact
- Include specific measurements (minimum 20x20 ft capture area, 15 ft ceiling clearance, etc.)
- Include power requirements (4x dedicated 20A circuits within 50 ft)

Pre-Scan Instructions:
- Open with date, time, location
- Sections: Arrival & Check-In, What to Expect, Wardrobe Guidelines, Hair & Makeup, Timeline, Contact
- Friendly but precise. Talent should know exactly what happens and when.
- Include specific timing ("Each scan takes approximately 3 minutes")

Scope of Work:
- Open with a project summary paragraph
- Sections: Project Overview, Capture Methods, Deliverables, Timeline, Terms
- Reference specific technology when relevant
- Describe capture methods technically but accessibly (see Sagamore Hill example below)

REFERENCE — here is an example of excellent NYCAP writing from a real scope of work:

"A collection of objects from Sagamore Hill National Historic Site will be digitally captured through high-resolution 3D scanning for the purpose of 3D reproduction. The client will be responsible for all model cleanup and 3D printing.

The collection includes a diverse range of artifacts — bronze sculptures, plaster casts and reliefs, decorative metalwork, ceramics, enameled vessels, gilded presentation pieces, jewelry, and painted wooden objects — spanning a wide variety of materials, surface finishes, and scales.

Each object will be evaluated individually and assigned the capture method best suited to its surface characteristics, size, and complexity. No method requires physical contact with any object. Results will vary based on material properties — highly reflective, dark, translucent, or glossy surfaces each respond differently to scanning technologies and may present inherent challenges.

Capture Methods

Photogrammetry — Overlapping high-resolution photographs captured from multiple angles are computationally processed to generate a detailed, full-color 3D model. Photogrammetry excels at capturing color and texture and is adaptable across a wide range of object sizes.

Artec Space Spider II — A handheld structured-light scanner that projects a light pattern onto the object's surface to build a highly accurate 3D mesh in real time, with geometric precision up to 0.05mm. It is especially well suited to fine surface detail such as inscriptions, relief carving, and organic textures."

CRITICAL RULES:
- Do NOT include letterhead, address, title, or "Prepared for" lines — the app adds those automatically.
- Start directly with the document body content.
- Respond with ONLY the document text. No markdown code fences, no commentary, no preamble.
- Keep total length reasonable: 1-2 pages when printed. Site requirements and pre-scan docs should be ~300-500 words. Scopes of work can be longer (~500-800 words).`;
